import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "./api";
import { apiErrorCopy } from "../shared/api-error-copy";

type InviteStatus = "active" | "used" | "expired" | "disabled";

interface PilotStatus { intake: "open" | "paused"; revision: number; available: boolean; participants: number; issued: number; redeemed: number; }

interface OperatorContext {
  pilot?: PilotStatus | null;
  operator: { email: string };
  environment: string;
  limits: {
    quantityMax: number;
    maxUsesMax: number;
    validDaysMax: number;
    internalNoteMaxLength: number;
  };
}

interface InviteListItem {
  inviteId: string;
  internalNote: string;
  createdAt: string;
  expiresAt: string | null;
  disabledAt: string | null;
  uses: number;
  maxUses: number;
  status: InviteStatus;
}

interface CreatedInvite {
  inviteId: string;
  code: string;
  expiresAt: string | null;
  maxUses: number;
}

type PairReviewState = "pending_review" | "approved" | "deferred" | "rejected";

interface PairParticipant {
  participantId: string;
  participationStatus: string;
  enteredAt: string | null;
  activeEligibilityState: string;
  actionRequiredReasons: string[];
  romanticModeAvailable: boolean;
  selectionAllowed: boolean;
}

interface PairEvaluationRead {
  evaluationId: string;
  pairKey: string;
  participantAAccountId: string;
  participantBAccountId: string;
  adapterContractVersion: string;
  registryVersion: string;
  specificationVersion: string;
  evaluationState: "HARD_CONFLICT" | "PENDING_REQUIRED_EVIDENCE" | "EVALUABLE";
  reviewState: PairReviewState;
  createdAt: string;
  reviewUpdatedAt: string | null;
  evaluation: StructuredPairEvaluation;
  currentOperationalEligibility: {
    deliverableForLaterReview: boolean;
    participantA: PairParticipant & { accountStatus: string; participationRevision: number | null };
    participantB: PairParticipant & { accountStatus: string; participationRevision: number | null };
    reciprocalPairEligibility: {
      eligible: boolean;
      reasonCodes: readonly string[];
    };
  };
  reviewHistory?: Array<{
    id: string;
    next_state: PairReviewState;
    internal_note: string;
    actor_email: string;
    created_at: string;
  }>;
  matchOffer?: OperatorMatchOffer | null;
}

interface OperatorMatchOffer {
  offerId: string;
  evaluationId: string;
  deliveryContractVersion: string;
  evaluationState: string;
  reviewState: PairReviewState;
  publishedAt: string;
  outcomeUpdatedAt: string;
  outcome: "pending_participant_responses" | "mutual_acceptance" | "closed_without_match";
  participantA: { side: "A"; response: "pending" | "accepted" | "declined"; projectionFingerprint: string; profileRevision: number; preferencesRevision: number; locationRevision: number; projectionLocale: string; explanationTemplateIds: string[] };
  participantB: { side: "B"; response: "pending" | "accepted" | "declined"; projectionFingerprint: string; profileRevision: number; preferencesRevision: number; locationRevision: number; projectionLocale: string; explanationTemplateIds: string[] };
  currentOperationalEligibility: {
    participantA: { participationStatus: string; activeEligibilityState: string; actionRequiredReasons: string[] };
    participantB: { participationStatus: string; activeEligibilityState: string; actionRequiredReasons: string[] };
  };
  contactUnlock: "not_implemented";
  notifications: "not_implemented";
}

interface StructuredPairEvaluation {
  pairEvaluationState: "HARD_CONFLICT" | "PENDING_REQUIRED_EVIDENCE" | "EVALUABLE";
  rankabilityState?: "RANKABLE" | "LOW_CONFIDENCE" | "DEFERRED_INSUFFICIENT_EVIDENCE";
  hardBlockResults: Array<{ ruleId: string; resultState: string; reasonCode?: string }>;
  needSupplyResults: {
    A_received_from_B: Array<{ ruleId: string; resultState: string; direction: string }>;
    B_received_from_A: Array<{ ruleId: string; resultState: string; direction: string }>;
  };
  cautionResults: Array<{ ruleId: string; resultState: string; participantVisibleState: string; evidenceQuality: string }>;
  missingEvidence: unknown[];
  unknownEvidence: unknown[];
  noPositiveEvidence: Array<{ ruleId: string; direction: string }>;
  participantExplanationCandidates: Array<{
    candidateType: string;
    ruleId: string;
    direction: string;
    participantPerspectives: string[];
    confidenceEligibility: string;
  }>;
}

const reviewStateLabels: Record<PairReviewState, string> = {
  pending_review: "待内部评审",
  approved: "内部通过",
  deferred: "暂缓",
  rejected: "内部拒绝",
};

async function operatorApi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  if (init.method && !["GET", "HEAD"].includes(init.method)) headers.set("X-Operator-Action", "confirmed");
  const response = await fetch(path, { ...init, headers, credentials: "same-origin", cache: "no-store" });
  const payload = await response.json().catch(() => null) as {
    error?: { code?: string; message?: string; details?: unknown };
  } | null;
  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload?.error?.code ?? "request_failed",
      apiErrorCopy(payload?.error?.message ?? "请求未能完成"),
      payload?.error?.details,
    );
  }
  return payload as T;
}

function ConfirmDialog({ open, title, body, confirmLabel, destructive = false, busy, onCancel, onConfirm }: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return <dialog ref={dialogRef} className="operator-dialog" onCancel={(event) => { event.preventDefault(); if (!busy) onCancel(); }}>
    <h2>{title}</h2>
    <div className="dialog-copy">{body}</div>
    <div className="dialog-actions">
      <button className="button secondary" disabled={busy} onClick={onCancel}>取消</button>
      <button className={`button ${destructive ? "destructive" : "primary"}`} disabled={busy} onClick={onConfirm}>
        {busy ? "正在处理" : confirmLabel}
      </button>
    </div>
  </dialog>;
}

const statusLabels: Record<InviteStatus, string> = {
  active: "可使用",
  used: "已用完",
  expired: "已过期",
  disabled: "已禁用",
};

function shortId(value: string): string {
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString("zh-CN", { dateStyle: "medium", timeStyle: "short" }) : "不限期";
}

export function OperatorApp() {
  const [context, setContext] = useState<OperatorContext | null>(null);
  const [invites, setInvites] = useState<InviteListItem[]>([]);
  const [filter, setFilter] = useState<InviteStatus | "all">("all");
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState("");
  const [actionError, setActionError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [maxUses, setMaxUses] = useState(1);
  const [expiryMode, setExpiryMode] = useState<"days" | "date">("days");
  const [validDays, setValidDays] = useState(14);
  const [expiresAt, setExpiresAt] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmIntake, setConfirmIntake] = useState(false);
  const createKey = useRef<string | null>(null);
  const [disableTarget, setDisableTarget] = useState<InviteListItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedInvite[]>([]);
  const [copyMessage, setCopyMessage] = useState("");
  const [pairParticipants, setPairParticipants] = useState<PairParticipant[]>([]);
  const [pairEvaluations, setPairEvaluations] = useState<PairEvaluationRead[]>([]);
  const [selectedParticipantA, setSelectedParticipantA] = useState("");
  const [selectedParticipantB, setSelectedParticipantB] = useState("");
  const [selectedEvaluation, setSelectedEvaluation] = useState<PairEvaluationRead | null>(null);
  const [pairBusy, setPairBusy] = useState(false);
  const [pairError, setPairError] = useState("");
  const [reviewNote, setReviewNote] = useState("");
  const [confirmPublishOffer, setConfirmPublishOffer] = useState(false);

  const load = useCallback(async () => {
    setActionError("");
    const contextResult = await operatorApi<OperatorContext>("/api/v1/operator/context");
    const [inviteResult, participantResult, evaluationResult] = await Promise.all([
      operatorApi<{ invites: InviteListItem[] }>("/api/v1/operator/invites"),
      contextResult.pilot ? Promise.resolve({participants:[]}) : operatorApi<{ participants: PairParticipant[] }>("/api/v1/operator/pair-evaluations/participants"),
      contextResult.pilot ? Promise.resolve({evaluations:[]}) : operatorApi<{ evaluations: PairEvaluationRead[] }>("/api/v1/operator/pair-evaluations"),
    ]);
    setContext(contextResult);
    setInvites(inviteResult.invites);
    setPairParticipants(participantResult.participants);
    setPairEvaluations(evaluationResult.evaluations);
  }, []);

  useEffect(() => {
    void load().catch((error) => setFatalError(error instanceof Error ? error.message : "管理界面无法加载"))
      .finally(() => setLoading(false));
  }, [load]);

  const visibleInvites = useMemo(
    () => filter === "all" ? invites : invites.filter((invite) => invite.status === filter),
    [filter, invites],
  );

  const create = async () => {
    if (!context) return;
    setBusy(true);
    setActionError("");
    try {
      const result = await operatorApi<{ created: CreatedInvite[] }>("/api/v1/operator/invites", {
        method: "POST",
        headers: { "Idempotency-Key": createKey.current ??= crypto.randomUUID().replaceAll("-", "") },
        body: JSON.stringify({
          quantity,
          maxUses,
          internalNote,
          ...(expiryMode === "days" ? { validDays } : { expiresAt: new Date(expiresAt).toISOString() }),
          confirmedEnvironment: context.environment,
        }),
      });
      setCreated(result.created);
      createKey.current = null;
      setConfirmCreate(false);
      setInternalNote("");
      await load().catch(() => setActionError("邀请码已创建，请先保存上方明文，再刷新记录。"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setActionError((error instanceof Error ? error.message : "未能确认创建结果") + "；结果不确定时请先刷新记录，不要重复创建。");
      setConfirmCreate(false);
    } finally {
      setBusy(false);
    }
  };

  const changeIntake = async () => {
    if (!context?.pilot) return;
    setBusy(true); setActionError("");
    try {
      await operatorApi('/api/v1/operator/pilot/intake', {method:'PUT',body:JSON.stringify({
        intake:context.pilot.intake==='open'?'paused':'open',expectedRevision:context.pilot.revision,confirmedEnvironment:context.environment,
      })});
      await load();
    } catch (error) {
      setActionError((error instanceof Error ? error.message : '未能确认状态') + '；请刷新确认当前状态后再操作。');
    } finally { setConfirmIntake(false); setBusy(false); }
  };

  const disable = async () => {
    if (!context || !disableTarget) return;
    setBusy(true);
    setActionError("");
    try {
      await operatorApi(`/api/v1/operator/invites/${disableTarget.inviteId}/disable`, {
        method: "POST",
        body: JSON.stringify({
          confirmedEnvironment: context.environment,
          confirmedInviteId: disableTarget.inviteId,
        }),
      });
      setDisableTarget(null);
      await load();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "邀请码禁用失败");
      setDisableTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const runPairEvaluation = async () => {
    if (!selectedParticipantA || !selectedParticipantB) return;
    setPairBusy(true);
    setPairError("");
    try {
      const result = await operatorApi<{ created: boolean; evaluation: PairEvaluationRead }>(
        "/api/v1/operator/pair-evaluations",
        {
          method: "POST",
          body: JSON.stringify({
            participantAAccountId: selectedParticipantA,
            participantBAccountId: selectedParticipantB,
          }),
        },
      );
      setSelectedEvaluation(result.evaluation);
      await load();
    } catch (error) {
      setPairError(error instanceof Error ? error.message : "配对评估未能完成");
    } finally {
      setPairBusy(false);
    }
  };

  const openPairEvaluation = async (evaluationId: string) => {
    setPairBusy(true);
    setPairError("");
    try {
      const result = await operatorApi<{ evaluation: PairEvaluationRead }>(
        `/api/v1/operator/pair-evaluations/${evaluationId}`,
      );
      setSelectedEvaluation(result.evaluation);
      setReviewNote("");
    } catch (error) {
      setPairError(error instanceof Error ? error.message : "无法加载配对评估");
    } finally {
      setPairBusy(false);
    }
  };

  const updateReviewState = async (nextState: PairReviewState) => {
    if (!selectedEvaluation) return;
    setPairBusy(true);
    setPairError("");
    try {
      const result = await operatorApi<{ evaluation: PairEvaluationRead }>(
        `/api/v1/operator/pair-evaluations/${selectedEvaluation.evaluationId}/review`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID().replaceAll("-", "") },
          body: JSON.stringify({
            expectedReviewState: selectedEvaluation.reviewState,
            nextState,
            internalNote: reviewNote,
          }),
        },
      );
      setSelectedEvaluation(result.evaluation);
      setReviewNote("");
      await load();
    } catch (error) {
      setPairError(error instanceof Error ? error.message : "内部评审状态未能保存");
    } finally {
      setPairBusy(false);
    }
  };

  const publishMatchOffer = async () => {
    if (!selectedEvaluation) return;
    setPairBusy(true);
    setPairError("");
    try {
      const result = await operatorApi<{ offer: OperatorMatchOffer }>(
        `/api/v1/operator/pair-evaluations/${selectedEvaluation.evaluationId}/match-offer`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID().replaceAll("-", "") },
          body: JSON.stringify({ locale: "zh" }),
        },
      );
      setConfirmPublishOffer(false);
      setSelectedEvaluation((current) => current ? { ...current, matchOffer: result.offer } : current);
      await load();
    } catch (error) {
      setPairError(error instanceof Error ? error.message : "认识邀请未能发布");
      setConfirmPublishOffer(false);
    } finally {
      setPairBusy(false);
    }
  };

  const copy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(message);
      window.setTimeout(() => setCopyMessage(""), 2400);
    } catch {
      setCopyMessage("浏览器未允许复制，请手动选择文本");
    }
  };

  if (loading) return <main className="center-state"><p>正在验证管理权限</p></main>;
  if (fatalError || !context) return <main className="center-state"><h1>无法进入管理界面</h1><p>{fatalError || "身份信息不可用"}</p></main>;
  const isProduction = context.environment === "production";
  const expiryValid = expiryMode === "days" ? validDays >= 1 && validDays <= context.limits.validDaysMax : Boolean(expiresAt);

  return <div className="operator-shell">
    <header className={`environment-banner ${isProduction ? "production" : "development"}`}>
      <strong>{isProduction ? "正式环境" : "开发环境"}</strong>
      <span>{isProduction ? "正在管理正式环境的邀请码，所有操作都会留下审计记录。" : "本地开发环境，不会改动正式环境的邀请码。"}</span>
    </header>
    <main className="operator-main">
      <header className="operator-header">
        <h1>邀请码</h1>
        <p className="operator-identity">{context.operator.email}</p>
      </header>

      {context.pilot && <section className="operator-section" aria-labelledby="pilot-title">
        <div className="section-heading"><h2 id="pilot-title">首批试点</h2>
          <p role="status">{!context.pilot.available ? '当前安全配置未开放' : context.pilot.intake === 'open' ? '已开放邀请注册' : '已暂停新注册'}</p>
          <p>先邀请 3–5 人，达到约 10 人时再回看参与情况。各地参与者可能需要等待人选，系统会按现有规则配对。</p>
          <p>已发出 {context.pilot.issued} 个邀请码 · 已使用 {context.pilot.redeemed} 个 · 当前 {context.pilot.participants} 位参与者</p>
          <p>暂停后，已发出但未使用的邀请码暂不能注册；已有参与者仍可正常使用、撤回联系方式和删除账户。</p>
        </div>
        <button className="button secondary" disabled={busy || !context.pilot.available} onClick={() => setConfirmIntake(true)}>
          {context.pilot.intake === 'open' ? '暂停新注册' : '恢复新注册'}
        </button>
        <button className="text-button" disabled={busy} onClick={() => void load().catch(() => setActionError('刷新失败，请稍后再试'))}>刷新状态</button>
      </section>}

      {created.length > 0 && <section className="created-invites" aria-labelledby="created-title">
        <div className="created-heading">
          <h2 id="created-title">保存刚创建的邀请码</h2>
          <button className="button secondary" onClick={() => void copy(created.map((invite) => invite.code).join("\n"), "已复制全部邀请码")}>复制全部</button>
        </div>
        <p>刷新或离开页面后，邀请码明文无法再次获取。</p>
        <ul className="created-code-list">
          {created.map((invite) => <li key={invite.inviteId}>
            <code>{invite.code}</code>
            <button className="text-button" onClick={() => void copy(invite.code, "邀请码已复制")}>复制</button>
          </li>)}
        </ul>
        <button className="text-button" onClick={() => setCreated([])}>我已经安全保存</button>
      </section>}

      {copyMessage && <p className="operator-feedback" role="status">{copyMessage}</p>}
      {actionError && <p className="error-message operator-error" role="alert">{actionError}</p>}
      {pairError && <p className="error-message operator-error" role="alert">{pairError}</p>}

      {!context.pilot && <section className="operator-section pair-evaluation-workspace" aria-labelledby="pair-evaluation-title">
        <div className="section-heading">
          <h2 id="pair-evaluation-title">手动配对评估</h2>
          <p>选择两位当前符合参与条件的人进行内部评估。这里不排序、不提供兼容度分数，也不会自动发送结果。</p>
        </div>
        <div className="pair-selection-grid">
          <label>
            <span>参与者 A</span>
            <select value={selectedParticipantA} onChange={(event) => setSelectedParticipantA(event.target.value)}>
              <option value="">选择一位参与者</option>
              {pairParticipants.map((participant) => <option key={participant.participantId} value={participant.participantId} disabled={!participant.selectionAllowed}>
                {shortId(participant.participantId)} · {participant.selectionAllowed ? "可评估" : "当前不可选"}
              </option>)}
            </select>
          </label>
          <label>
            <span>参与者 B</span>
            <select value={selectedParticipantB} onChange={(event) => setSelectedParticipantB(event.target.value)}>
              <option value="">选择一位参与者</option>
              {pairParticipants.map((participant) => <option key={participant.participantId} value={participant.participantId} disabled={!participant.selectionAllowed || participant.participantId === selectedParticipantA}>
                {shortId(participant.participantId)} · {participant.selectionAllowed ? "可评估" : "当前不可选"}
              </option>)}
            </select>
          </label>
        </div>
        {pairParticipants.length === 0 && <p className="operator-empty">当前没有进入匹配池的参与者。</p>}
        <div className="pair-selection-meta">
          <span>{pairParticipants.filter((participant) => participant.selectionAllowed).length} 位参与者当前满足手动评估资格</span>
          <button className="text-button" onClick={() => void load()}>刷新参与者列表</button>
        </div>
        <button className="button primary" disabled={pairBusy || !selectedParticipantA || !selectedParticipantB || selectedParticipantA === selectedParticipantB} onClick={() => void runPairEvaluation()}>
          {pairBusy ? "正在读取并评估" : "开始或查看评估"}
        </button>

        {pairEvaluations.length > 0 && <div className="pair-evaluation-list">
          <h3>最近的内部评估</h3>
          <ul>
            {pairEvaluations.map((item) => <li key={item.evaluationId}>
              <button className="pair-evaluation-list-item" onClick={() => void openPairEvaluation(item.evaluationId)}>
                <span><code>{shortId(item.evaluationId)}</code><small>{shortId(item.participantAAccountId)} × {shortId(item.participantBAccountId)}</small></span>
                <span><strong>{item.evaluationState}</strong><small>{reviewStateLabels[item.reviewState]}</small></span>
              </button>
            </li>)}
          </ul>
        </div>}

        {selectedEvaluation && <section className="pair-evaluation-result" aria-labelledby="pair-result-title">
          <div className="pair-result-heading">
            <h3 id="pair-result-title">{selectedEvaluation.evaluationState}</h3>
            <code>{shortId(selectedEvaluation.evaluationId)}</code>
          </div>
          <dl className="pair-result-meta">
            <div><dt>评估版本</dt><dd>{selectedEvaluation.specificationVersion}</dd></div>
            <div><dt>当前评审</dt><dd>{reviewStateLabels[selectedEvaluation.reviewState]}</dd></div>
            <div><dt>当前运营状态</dt><dd>{selectedEvaluation.currentOperationalEligibility.deliverableForLaterReview ? "双方仍具备资格" : "至少一方当前不符合发布条件"}</dd></div>
            <div><dt>相互开放边界</dt><dd>{selectedEvaluation.currentOperationalEligibility.reciprocalPairEligibility.eligible
              ? "双方均符合彼此的范围设置" : `当前不能发布：${selectedEvaluation.currentOperationalEligibility.reciprocalPairEligibility.reasonCodes.join("、")}`}</dd></div>
            <div><dt>配对标识</dt><dd><code>{shortId(selectedEvaluation.pairKey)}</code></dd></div>
          </dl>
          <div className="pair-result-grid">
            <div><h4>硬性条件</h4><ul>{selectedEvaluation.evaluation.hardBlockResults.map((item) => <li key={item.ruleId}><strong>{item.resultState}</strong> · {item.ruleId}{item.reasonCode ? ` · ${item.reasonCode}` : ""}</li>)}</ul></div>
            <div><h4>支持配对的依据</h4><ul>{selectedEvaluation.evaluation.participantExplanationCandidates.filter((item) => item.candidateType === "positive_reason").map((item) => <li key={`${item.ruleId}-${item.direction}`}>{item.ruleId} · {item.direction} · {item.confidenceEligibility}</li>)}</ul></div>
            <div><h4>谨慎提示</h4><ul>{selectedEvaluation.evaluation.cautionResults.map((item) => <li key={item.ruleId}><strong>{item.resultState}</strong> · {item.ruleId} · {item.evidenceQuality}</li>)}</ul></div>
            <div><h4>证据状态</h4><p>待确认 {selectedEvaluation.evaluation.missingEvidence.length} · 未知 {selectedEvaluation.evaluation.unknownEvidence.length} · 无正向证据 {selectedEvaluation.evaluation.noPositiveEvidence.length}</p><p>可向参与者展示的理由 {selectedEvaluation.evaluation.participantExplanationCandidates.length}</p></div>
          </div>
          <div className="pair-review-controls">
            <label><span>内部备注</span><textarea rows={3} maxLength={1000} value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} placeholder="仅供内部评审，请勿填写问卷原文或联系方式。" /></label>
            <div className="pair-review-actions">
              {(Object.keys(reviewStateLabels) as PairReviewState[]).filter((state) => state !== selectedEvaluation.reviewState).map((state) => <button key={state} className={state === "approved" ? "button primary" : "button secondary"} disabled={pairBusy} onClick={() => void updateReviewState(state)}>{reviewStateLabels[state]}</button>)}
            </div>
          </div>
          {!selectedEvaluation.matchOffer && selectedEvaluation.reviewState === "approved"
            && selectedEvaluation.evaluationState === "EVALUABLE" && <div className="match-offer-publish">
            <p className="field-help">发布后，双方会在各自账户中看到对方获准展示的资料，以及最多两条按固定模板生成的理由。联系方式不会解锁，也不会发送通知。</p>
            {!selectedEvaluation.currentOperationalEligibility.reciprocalPairEligibility.eligible && <p className="field-help">双方目前不满足彼此对性别、年龄段或地点的要求，无法发布。</p>}
            <button className="button primary" disabled={pairBusy || !selectedEvaluation.currentOperationalEligibility.deliverableForLaterReview}
              onClick={() => setConfirmPublishOffer(true)}>发布认识邀请</button>
          </div>}
          {selectedEvaluation.matchOffer && <section className="match-offer-operator" aria-labelledby="match-offer-operator-title">
            <h4 id="match-offer-operator-title">已发布的认识邀请</h4>
            <dl className="pair-result-meta match-offer-meta">
              <div><dt>当前结果</dt><dd>{selectedEvaluation.matchOffer.outcome === "mutual_acceptance" ? "双方都接受" : selectedEvaluation.matchOffer.outcome === "closed_without_match" ? "已关闭，未形成双方接受" : "等待双方回应"}</dd></div>
              <div><dt>A 的回应</dt><dd>{selectedEvaluation.matchOffer.participantA.response}</dd></div>
              <div><dt>B 的回应</dt><dd>{selectedEvaluation.matchOffer.participantB.response}</dd></div>
              <div><dt>联系方式</dt><dd>未解锁</dd></div>
            </dl>
            <p className="field-help">管理界面只显示状态、版本和审计信息，不显示参与者资料、问卷答案或理由正文。</p>
          </section>}
          {selectedEvaluation.reviewHistory && selectedEvaluation.reviewHistory.length > 0 && <div className="pair-review-history"><h4>评审记录</h4><ol>{selectedEvaluation.reviewHistory.map((event) => <li key={event.id}><strong>{reviewStateLabels[event.next_state]}</strong><span>{event.actor_email} · {formatDate(event.created_at)}</span>{event.internal_note && <small>{event.internal_note}</small>}</li>)}</ol></div>}
          <p className="field-help">“内部通过”只记录评审结论，不会向参与者展示，也不会代替参与者表达意愿或发送通知。</p>
        </section>}
      </section>}

      <section className="operator-section" aria-labelledby="create-title">
        <div className="section-heading"><h2 id="create-title">创建邀请码</h2><p>明文只在创建成功后的当前页面出现一次。</p></div>
        <div className="operator-form-grid">
          <label><span>数量</span><input type="number" min="1" max={context.limits.quantityMax} value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} /></label>
          <label><span>每个最多使用几次</span><input type="number" min="1" max={context.limits.maxUsesMax} value={maxUses} onChange={(event) => setMaxUses(Number(event.target.value))} /></label>
        </div>
        <fieldset className="operator-expiry">
          <legend>有效期</legend>
          <label className="operator-radio"><input type="radio" checked={expiryMode === "days"} onChange={() => setExpiryMode("days")} /><span>按有效天数</span></label>
          {expiryMode === "days" && <input aria-label="有效天数" type="number" min="1" max={context.limits.validDaysMax} value={validDays} onChange={(event) => setValidDays(Number(event.target.value))} />}
          <label className="operator-radio"><input type="radio" checked={expiryMode === "date"} onChange={() => setExpiryMode("date")} /><span>指定到期时间</span></label>
          {expiryMode === "date" && <input aria-label="到期时间" type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />}
        </fieldset>
        <label className="operator-note"><span>内部备注</span><textarea rows={3} maxLength={context.limits.internalNoteMaxLength} value={internalNote} onChange={(event) => setInternalNote(event.target.value)} /><small>{internalNote.length} / {context.limits.internalNoteMaxLength} · 参与者不可见</small></label>
        <button className="button primary" disabled={busy || Boolean(context.pilot && (!context.pilot.available || context.pilot.intake !== "open")) || !expiryValid || quantity < 1 || quantity > context.limits.quantityMax || maxUses < 1 || maxUses > context.limits.maxUsesMax} onClick={() => setConfirmCreate(true)}>检查并创建</button>
      </section>

      <section className="operator-section invite-register" aria-labelledby="list-title">
        <div className="invite-list-heading">
          <div className="section-heading"><h2 id="list-title">邀请码记录</h2></div>
          <label><span>筛选</span><select value={filter} onChange={(event) => setFilter(event.target.value as InviteStatus | "all")}><option value="all">全部</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        </div>
        {visibleInvites.length === 0 ? <p className="operator-empty">当前没有符合条件的邀请码。</p> : <div className="invite-table-wrap"><table className="invite-table">
          <thead><tr><th>邀请码编号</th><th>内部备注</th><th>创建</th><th>到期</th><th>使用</th><th>状态</th><th><span className="visually-hidden">操作</span></th></tr></thead>
          <tbody>{visibleInvites.map((invite) => <tr key={invite.inviteId}>
            <td data-label="邀请码编号"><code title={invite.inviteId}>{shortId(invite.inviteId)}</code></td>
            <td data-label="内部备注">{invite.internalNote || "—"}</td>
            <td data-label="创建">{formatDate(invite.createdAt)}</td>
            <td data-label="到期">{formatDate(invite.expiresAt)}</td>
            <td data-label="使用">{invite.uses} / {invite.maxUses}</td>
            <td data-label="状态"><span className={`invite-status ${invite.status}`}>{statusLabels[invite.status]}</span></td>
            <td data-label="操作">{invite.status === "active" && <button className="text-button danger-text" onClick={() => setDisableTarget(invite)}>禁用</button>}</td>
          </tr>)}</tbody>
        </table></div>}
      </section>
    </main>

    <ConfirmDialog open={confirmIntake} title={context.pilot?.intake === 'open' ? '暂停新注册' : '恢复新注册'}
      body={<p>{context.pilot?.intake === 'open' ? '这会暂停创建邀请码及未使用邀请码的注册。已有参与者的使用、撤回与删除不受影响。' : '这会恢复单次邀请码的创建与注册，请继续按小批次个别邀请。'}</p>}
      confirmLabel="确认更改" busy={busy} onCancel={() => setConfirmIntake(false)} onConfirm={() => void changeIntake()} />
    <ConfirmDialog
      open={confirmCreate}
      title="确认创建邀请码"
      body={<><p>目标环境：<strong>{context.environment}</strong></p><p>将创建 {quantity} 个邀请码，每个最多使用 {maxUses} 次。创建后请立即保存明文。</p></>}
      confirmLabel={isProduction ? "在正式环境创建" : "确认创建"}
      busy={busy}
      onCancel={() => setConfirmCreate(false)}
      onConfirm={() => void create()}
    />
    <ConfirmDialog
      open={Boolean(disableTarget)}
      title="禁用这个邀请码"
      body={<p>邀请码编号：<strong>{disableTarget ? shortId(disableTarget.inviteId) : ""}</strong>。禁用后不能再用于注册。</p>}
      confirmLabel="确认禁用"
      destructive
      busy={busy}
      onCancel={() => setDisableTarget(null)}
      onConfirm={() => void disable()}
    />
    <ConfirmDialog
      open={confirmPublishOffer}
      title="确认发布认识邀请"
      body={<p>双方会在各自账户中看到对方获准展示的资料，以及按固定模板生成的理由，之后各自决定是否回应。联系方式不会解锁，也不会发送通知或自动匹配。</p>}
      confirmLabel="确认发布"
      busy={pairBusy}
      onCancel={() => setConfirmPublishOffer(false)}
      onConfirm={() => void publishMatchOffer()}
    />
  </div>;
}
